import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

const NAV_ID = 'app-sidebar';
/** Keep in step with the drawer breakpoint in globals.css. */
const DRAWER_QUERY = '(max-width: 768px)';

/**
 * App shell. Above the CSS breakpoint the sidebar is a static column; below it
 * the sidebar is an off-canvas drawer opened by the header's menu toggle. The
 * open state is inert on wide screens (the CSS ignores it).
 */
export function MainLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const closeNav = useCallback((returnFocus: boolean) => {
    setNavOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  }, []);

  // Past the breakpoint (e.g. a tablet rotating) the drawer is no longer a
  // drawer: close it so the focus trap and backdrop state don't linger.
  useEffect(() => {
    if (!navOpen || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DRAWER_QUERY);
    const closeForWide = () => {
      // The Close menu button disappears at this width; if it (or any drawer
      // control) held focus, hand focus to the first nav link, not <body>.
      const drawer = document.getElementById(NAV_ID);
      const hadFocus = drawer?.contains(document.activeElement) ?? false;
      setNavOpen(false);
      if (hadFocus) drawer?.querySelector<HTMLElement>('a[href]')?.focus();
    };
    // The viewport may have widened between the open and this effect running.
    if (!mql.matches) {
      closeForWide();
      return;
    }
    const onChange = (e: MediaQueryListEvent) => {
      if (!e.matches) closeForWide();
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [navOpen]);

  useEffect(() => {
    if (!navOpen) return;
    const drawer = () => document.getElementById(NAV_ID);
    const links = () =>
      Array.from(drawer()?.querySelectorAll<HTMLElement>('a[href], button') ?? []).filter(
        (el) => !(el instanceof HTMLButtonElement && el.disabled)
      );
    // Move focus into the drawer on open, then keep Tab inside it (the rest
    // of the page is covered by the backdrop).
    drawer()?.querySelector<HTMLElement>('a[href]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeNav(true);
        return;
      }
      if (e.key !== 'Tab') return;
      const items = links();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!drawer()?.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen, closeNav]);

  return (
    <div className="app-shell">
      <Sidebar
        id={NAV_ID}
        open={navOpen}
        onNavigate={() => closeNav(navOpen)}
        onClose={() => closeNav(true)}
      />
      {navOpen && (
        <div className="app-nav-backdrop" aria-hidden="true" onClick={() => closeNav(true)} />
      )}
      <div className="app-main">
        <Header
          nav={{
            id: NAV_ID,
            open: navOpen,
            toggleRef,
            onToggle: () => setNavOpen((o) => !o),
          }}
        />
        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
